import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { isWithinInterval } from 'date-fns';
import { ListDealsRequest, dealsQueryKeys } from '@jucy-ui/common/services';
import { env } from '@/env';
import { CmsDeal, cmsDealSchema } from '@/services/cms/models/CmsDeal';

type UseGetDealsOptions = UseQueryOptions<CmsDeal[], Error, CmsDeal[], string[]> & Omit<ListDealsRequest, 'accountId'>;

export const useGetMarketingDeals = ({ tags, region, code, type, ...options }: Omit<UseGetDealsOptions, 'queryKey' | 'queryFn'>) => {
    const dealRequest = {
        tags,
        region,
        code,
        type,
    };
    return useQuery({
        queryKey: dealsQueryKeys.list({ accountId: `${env.NEXT_PUBLIC_RENTALS_API_ACCOUNT_KEY}`, ...dealRequest }),
        queryFn: async () => getMarketingDeals(dealRequest),
        ...options,
    });
};


export const getMarketingDeals = async (dealRequest: Omit<ListDealsRequest, 'accountId'>) => {
    const url = new URL('/api/deals', env.NEXT_PUBLIC_RENTALS_API_BASE_URL);
    url.searchParams.set('accountId', `${env.NEXT_PUBLIC_RENTALS_API_ACCOUNT_KEY}`);
    if (dealRequest.tags?.size && dealRequest.tags?.size > 0) {
        url.searchParams.set('tags', Array.from(dealRequest.tags).join(','));
    }
    if (dealRequest.region) {
        url.searchParams.set('region', dealRequest.region);
    }
    if (dealRequest.type) {
        url.searchParams.set('type', dealRequest.type);
    }
    if (dealRequest.code) {
        url.searchParams.set('code', dealRequest.code);
    }
    const response = await fetch(url);
    const responseData = (await response.json()) as { items?: CmsDeal[] };
    if (!responseData?.items?.length) {
        return [];
    }
    return responseData.items
        .map((deal) => {
            try {
                return cmsDealSchema.parse({
                    ...deal,
                    regions: [],
                    visible: true,
                });
            } catch (e) {
                console.error('Error parsing deal', e);
                return null;
            }
        })
        .filter((deal) => deal && isMarketingDeal(deal)) as CmsDeal[];
};

const isMarketingDeal = (deal: CmsDeal): boolean => {
    if (
        !(
            deal.marketingStartDate &&
            deal.marketingEndDate &&
            isWithinInterval(new Date(), {
                start: deal.marketingStartDate,
                end: deal.marketingEndDate,
            })
        )
    ) {
        return false;
    }
    if (!deal.imgUrl) {
        return false;
    }

    if (!deal.summary) {
        return false;
    }

    return deal?.visible !== false;
};
